// src/pages/Pagos.js
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useGlobalContext } from "../Config";

// --- util fecha (mismo parser que usas en Gestión) ---
const parseBackendDate = (val) => {
  if (!val) return null;
  if (val instanceof Date)
    return DateTime.fromJSDate(val, { zone: "America/Santiago" });

  if (typeof val !== "string") return null;
  const trimmed = val.trim();
  const cleaned = trimmed.replace(/\sCLST|\sCLT/g, "");

  let dt = DateTime.fromFormat(cleaned, "ccc LLL dd HH:mm:ss yyyy", {
    zone: "America/Santiago",
    locale: "en",
  });
  if (!dt.isValid) {
    dt = DateTime.fromFormat(cleaned, "ccc LLL d HH:mm:ss yyyy", {
      zone: "America/Santiago",
      locale: "en",
    });
  }
  if (!dt.isValid) dt = DateTime.fromISO(trimmed, { zone: "America/Santiago" });
  if (!dt.isValid) {
    const js = new Date(trimmed);
    if (!isNaN(js)) dt = DateTime.fromJSDate(js, { zone: "America/Santiago" });
  }
  return dt.isValid ? dt : null;
};

const formatDateCell = (val) => {
  const dt = parseBackendDate(val);
  return dt ? dt.toFormat("dd/MM/yyyy") : "—";
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  });

export default function Pagos() {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl, headerText } = useGlobalContext();
  const [searchParams] = useSearchParams();

  // folio puede venir por state o por query param
  const folioFromState = location.state?.folio;
  const folioFromQuery = searchParams.get("folio");
  const folio = folioFromState || folioFromQuery || "";

  // Para el primer grid reusamos el documento que venía desde Gestión
  const documento = location.state?.documento || null;
  const rowsDoc = useMemo(() => {
    if (
      Array.isArray(documento?.documentos) &&
      documento.documentos.length > 0
    ) {
      return documento.documentos;
    }
    return documento ? [documento] : [];
  }, [documento]);

  // estado de pagos
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagosRaw, setPagosRaw] = useState([]); // respuesta documentos (pagos)

  useEffect(() => {
    if (!folio) return;
    const fetchPagos = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${baseUrl}pagos/lista?folio=${encodeURIComponent(folio)}`,
          { headers: { ...headerText } }
        );
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`Error ${res.status}: ${text || "al obtener pagos"}`);
        }
        const data = await res.json();
        setPagosRaw(Array.isArray(data?.documentos) ? data.documentos : []);
      } catch (e) {
        setError(e.message || "No se pudo cargar el historial de pagos.");
      } finally {
        setLoading(false);
      }
    };
    fetchPagos();
  }, [baseUrl, headerText, folio]);

  // Mapeo a “Detalles de Pagos”
  const detallePagos = useMemo(() => {
    return (pagosRaw || []).map((p) => {
      const tipo = p.cheque
        ? "CHEQUE"
        : p.deposito
        ? "DEPÓSITO"
        : p.transferencia
        ? "TRANSFERENCIA"
        : p.webPay
        ? "WEBPAY"
        : "—";

      const detalle =
        p.cheque || p.deposito || p.transferencia || p.webpay || null;
      const monto = detalle?.monto ?? p.monto ?? 0;

      // Heurística:
      // - fechaPago: si es cheque y viene fechaDocumento -> esa; si no, la fecha del detalle; si no, la fecha top-level
      // - fechaIngreso: fecha top-level (cuando quedó registrado en el sistema)
      const fechaPago =
        (detalle && (detalle.fechaDocumento || detalle.fecha)) ||
        p.fecha ||
        null;
      const fechaIngreso = p.fecha || detalle?.fecha || null;

      return {
        tipoPago: tipo,
        monto,
        fechaPago,
        fechaIngreso,
      };
    });
  }, [pagosRaw]);

  return (
    <div style={{ padding: "1rem", background: "#f6f7f9", minHeight: "100vh" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Button
            label="Volver"
            icon="pi pi-arrow-left"
            outlined
            onClick={() => navigate("/documentos")}
            style={{
              borderRadius: "2rem",
              padding: "0.5rem 1rem",
              fontWeight: 600,
              color: "#0051a2",
              borderColor: "#0051a2",
            }}
          />
          <h2
            style={{
              margin: 0,
              fontWeight: 700,
              fontSize: "1.75rem",
              color: "#2c2c2c",
            }}
          >
            Historial de Pagos {folio ? `(Folio ${folio})` : ""}
          </h2>
        </div>
      </div>

      {/* 1) Mismo datatable que Gestión (si tenemos el documento en state) */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "1rem",
          marginBottom: "2rem",
        }}
      >
        <DataTable
          className="p-datatable-gridlines"
          value={rowsDoc}
          stripedRows
          emptyMessage="Sin datos."
        >
          <Column
            field="tipoDocumento.descripcion"
            header="Tipo Documento"
            style={{ minWidth: 150 }}
          />
          <Column field="folio" header="Folio" />
          <Column
            field="fechaEmision"
            header="Fecha de Emisión"
            body={(row) => formatDateCell(row.fechaEmision)}
          />
          <Column
            field="fechaVencimiento"
            header="Fecha de Vencimiento"
            body={(row) => formatDateCell(row.fechaVencimiento)}
          />
          <Column field="dias" header="Días" />
          <Column
            field="neto"
            header="Neto"
            body={(row) => formatCurrency(row.neto)}
          />
          <Column
            field="iva"
            header="IVA"
            body={(row) => formatCurrency(row.iva)}
          />
          <Column
            field="total"
            header="Total"
            body={(row) => formatCurrency(row.total)}
          />
          <Column
            field="deuda"
            header="Deuda"
            body={(row) => formatCurrency(row.deuda)}
          />
        </DataTable>
      </div>

      {/* 2) Detalles de Pagos */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1rem" }}>
        <h3 style={{ margin: 0, marginBottom: "1rem" }}>Detalles de Pagos</h3>

        {loading && <small>Cargando pagos…</small>}
        {error && <small style={{ color: "#e24c4c" }}>{error}</small>}

        <DataTable
          className="p-datatable-gridlines"
          value={detallePagos}
          stripedRows
          emptyMessage={
            loading ? " " : "No hay pagos registrados para este folio."
          }
        >
          <Column
            field="tipoPago"
            header="Tipo Pago"
            style={{ minWidth: 130 }}
          />
          <Column
            field="monto"
            header="Monto"
            body={(r) => formatCurrency(r.monto)}
          />
          <Column
            field="fechaPago"
            header="Fecha Pago"
            body={(r) => formatDateCell(r.fechaPago)}
          />
          <Column
            field="fechaIngreso"
            header="Fecha Ingreso"
            body={(r) => formatDateCell(r.fechaIngreso)}
          />
        </DataTable>
      </div>
    </div>
  );
}
